/**
 * Read file from system
 *
 * @param {Blob | File} file Object type Blop or File to read
 * @param {string} as Method used to read the file and return data
 * @returns Data from Blob or File
 */
export function readFile(file, as = 'text') {
  const type = {
    arrayBuffer: 'ArrayBuffer',
    dataURL: 'DataURL',
    string: 'BinaryString',
  }[as] || 'Text';

  return new Promise(function (resolve, reject) {
    const reader = new FileReader();

    reader.onload = function () {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader[`readAs${type}`](file);
  });
}

/**
 * Función para descargar un PDF desde una cadena Base64
 * @param {string} base64 - Cadena Base64 del PDF
 * @param {string} filename - Nombre del archivo a descargar (opcional)
 */
export function downloadPdfFromBase64(base64, filename = 'document.pdf') {
  // Crear un enlace temporal
  const link = document.createElement('a');
  
  // Decodificar el Base64
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { 
    type: 'application/pdf',
  });
  
  // Crear URL del blob
  const url = URL.createObjectURL(blob);
  
  // Configurar el enlace
  link.href = url;
  link.download = filename;
  link.target = '_blank';
  
  // Simular click para iniciar la descarga
  document.body.appendChild(link);
  link.click();
  
  // Limpiar
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Función para conocer el tipo de archivo a partir de su extensión
 * @param {string} input - nombre del archivo
 * @param {boolean} isMimeType - base64
 */
export const getFileType = (input, isMimeType = false) => {
  // Definimos los tipos principales y sus subtipos
  const DOCUMENT_TYPES = {
    pdf: ['pdf'],
    word: ['doc', 'docx', 'docm', 'dotx', 'odt'],
    excel: ['xls', 'xlsx', 'xlsm', 'xlsb', 'csv', 'ods'],
    powerpoint: ['ppt', 'pptx', 'pptm', 'potx', 'odp'],
  };

  const TYPE_CATEGORIES = {
    // Imágenes
    image: ['gif', 'jpg', 'jpeg', 'png', 'webp', 'bmp', 'svg', 'tiff', 'ico', 'heic'],
    // Archivos de texto
    text: ['txt', 'rtf', 'md', 'log', 'tex'],
    // Código
    code: ['js', 'jsx', 'ts', 'tsx', 'html', 'htm', 'css', 'scss', 'less', 'json', 'xml', 'php', 'py', 'java', 'c', 'cpp', 'h', 'sh'],
    // Comprimidos
    archive: ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'z'],
    // Multimedia
    audio: ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'wma'],
    video: ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'flv', 'webm', 'm4v', '3gp'],
  };

  if (isMimeType) {
    // Manejo de MIME types
    if (!input) return { 
      type: 'other', 
      subType: 'unknown',
    };
    
    const mime = input.toLowerCase();
    
    // Primero verificamos los tipos principales
    if (mime.startsWith('image/')) {
      return { 
        type: 'image', 
        subType: mime.split('/')[1] || 'image',
      };
    }
    if (mime.startsWith('audio/')) {
      return { 
        type: 'audio', 
        subType: mime.split('/')[1] || 'audio',
      };
    }
    if (mime.startsWith('video/')) {
      return { 
        type: 'video', 
        subType: mime.split('/')[1] || 'video',
      };
    }
    if (mime.includes('text/')) {
      return { 
        type: 'text', 
        subType: mime.split('/')[1] || 'text',
      };
    }

    // Luego los documentos
    if (mime.includes('pdf')) {
      return { 
        type: 'document', 
        subType: 'pdf',
      };
    }
    if (mime.includes('vnd.openxmlformats-officedocument.wordprocessingml.document') || mime.includes('msword')) {
      return { 
        type: 'document', 
        subType: 'word',
      };
    }
    if (mime.includes('vnd.openxmlformats-officedocument.spreadsheetml.sheet') || mime.includes('vnd.ms-excel')) {
      return { 
        type: 'document', 
        subType: 'excel',
      };
    }
    if (mime.includes('vnd.openxmlformats-officedocument.presentationml.presentation') || mime.includes('vnd.ms-powerpoint')) {
      return { 
        type: 'document', 
        subType: 'powerpoint',
      };
    }

    // Otros tipos
    if (mime.includes('json')) return { 
      type: 'code', 
      subType: 'json',
    };
    if (mime.includes('javascript')) return { 
      type: 'code', 
      subType: 'javascript',
    };
    if (mime.includes('zip') || mime.includes('compressed')) {
      return { 
        type: 'archive', 
        subType: 'zip',
      };
    }
    
    return { 
      type: 'other', 
      subType: 'unknown',
    };
  } else {
    // Manejo de extensiones de archivo
    const ext = String(input).toLowerCase().split('.').pop() || '';
    
    // Primero verificamos documentos
    for (const [subType, extensions] of Object.entries(DOCUMENT_TYPES)) {
      if (extensions.includes(ext)) {
        return { 
          type: 'document', 
          subType,
        };
      }
    }

    // Luego otros tipos
    for (const [type, extensions] of Object.entries(TYPE_CATEGORIES)) {
      if (extensions.includes(ext)) {
        return { 
          type, 
          subType: ext,
        };
      }
    }
    
    return { 
      type: 'other', 
      subType: ext || 'unknown',
    };
  }
};
